<template>
  <div>
    <div v-if="data">
      <div v-if="content.showFilterBar" class="pb-4">
        <div class="b-b-2 b-brand-1 py-4">
          <div class="flex items-end gap-x-4">
            <h2 v-if="!content.hideFilterTitle" class="subtitle-2">
              {{ content.filterTitle || $t.articleCategories }}
            </h2>
            <vf-carousel-elevate class-container="gap-x-8" :show-arrows="false">
              <base-button class="subtitle-5" :class="{ 'fw-bold underlined': !activeFilters.length }" @click="clearAllFilters">
                {{ $t.articleAllCategories }}
              </base-button>
              <base-button
                v-for="filter in filtersToShow"
                :key="filter.id"
                class="subtitle-5"
                :class="{ underlined: activeFilters.includes(filter.id) }"
                @click="toggleFilter(filter.id)"
              >
                {{ filter.label }}
              </base-button>
            </vf-carousel-elevate>
          </div>
        </div>
      </div>
      <div>
        <h2 v-if="hasTitle" class="pb-6 pt-8 text-center title-1">
          <span v-if="renderedTitleHtml" v-html="renderedTitleHtml" />
          <span v-else>{{ content.title }}</span>
        </h2>
      </div>
      <base-grid :class="paddingClasses" :cols="content.gridSize" gap="xs">
        <div
          v-for="(article, i) in articlesToShow"
          :key="article.id"
          :class="{ [offsetClasses]: (i + 2) % 3 === 0 }"
        >
          <vf-article-card-v2
            ref="articleRefs"
            v-bind="article"
            :image="{
              src: article.picture?.url,
              alt: article.picture.alt,
            }"
            :tags="[article.eyebrow]"
            :to="article.url"
          />
        </div>
      </base-grid>
      <div v-if="content.loadMore && articlesToShow.length < data?.numFound" class="py-6 text-center">
        <vf-button :loading="pending" @click="loadMore">
          {{ $t.articleLoadMore }}
        </vf-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ArticleGridContent } from '#types/components/cms/article-grid'

const { content } = defineProps<{ content: ArticleGridContent }>()

const { $feature } = useNuxtApp()
const renderedTitleHtml = computed(() => $feature.showTitleHtml && hasTextContent(content.titleHtml) ? content.titleHtml : '')
const hasTitle = computed(() => !!renderedTitleHtml.value || hasTextContent(content.title))

const elevatedBreakpoints = Object.entries(content.gridSize)
  .filter(([_, value]) => value === 3) // magic number, offset has to be applied only to 3cards grid
  .flatMap(([key]) => key)

const paddingClasses = elevatedBreakpoints.length && Object.values({
  sm: elevatedBreakpoints.includes('sm') && '<md:pb-10',
  md: elevatedBreakpoints.includes('md') && '~md:pb-10',
  lg: elevatedBreakpoints.includes('lg') && 'lg:pb-10',
}).filter(Boolean).join(' ')

const offsetClasses = elevatedBreakpoints.length && Object.values({
  sm: elevatedBreakpoints.includes('sm') && '<md:transform-translate-y-10',
  md: elevatedBreakpoints.includes('md') && '~md:transform-translate-y-10',
  lg: elevatedBreakpoints.includes('lg') && 'lg:transform-translate-y-10',
}).filter(Boolean).join(' ')

const {
  data,
  activeFilters,
  filtersToShow,
  clearAllFilters,
  toggleFilter,
  articlesToShow,
  pending,
  loadMore,
  articleRefs
} = useArticles({ ...content, kind: 'grid' })
</script>
